import { useEffect, useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../components';

const PHASE_COUNT = 2;

interface Phase {
  title: string;
  note: string;
  step: string;
}

/** 20px status node: filled + check when done, ring + dot when active, muted dot when pending. */
function StepNode({ done, active }: { done: boolean; active: boolean }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        borderRadius: '50%',
        flexShrink: 0,
        bgcolor: done ? 'primary.main' : active ? 'rgba(0, 71, 133, 0.10)' : 'background.default',
        border: done ? 'none' : '1px solid',
        borderColor: active ? 'primary.main' : '#CDD3E0',
      }}
    >
      {done ? (
        <CheckOutlined sx={{ fontSize: 12, color: '#fff' }} />
      ) : (
        <Box
          component="span"
          sx={{
            width: active ? 7 : 6,
            height: active ? 7 : 6,
            borderRadius: '50%',
            bgcolor: active ? 'primary.main' : '#CDD3E0',
          }}
        />
      )}
    </Box>
  );
}

/**
 * "Signing you in" transitional card (Prompt A2 / `Sign In.html` `SigningIn`).
 * PUBLIC, neutral brand, no app chrome. Shown while the whoami query is pending
 * — the window between MSAL returning a token (handled in the `main.tsx`
 * bootstrap) and the workspace being ready. An indeterminate top bar + a single
 * large spinner + a status line that advances through two phases
 * (authenticating → loading workspace) so the wait reads as progress. Customer
 * branding only applies once `whoami` resolves and the shell replaces this card.
 */
export function SigningInPage() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (phase >= PHASE_COUNT - 1) return;
    const id = setTimeout(() => setPhase((p) => p + 1), 2400);
    return () => clearTimeout(id);
  }, [phase]);

  const phases: [Phase, Phase] = [
    {
      title: t('signingIn.phaseAuthTitle'),
      note: t('signingIn.phaseAuthNote'),
      step: t('signingIn.stepAuthenticating'),
    },
    {
      title: t('signingIn.phaseWorkspaceTitle'),
      note: t('signingIn.phaseWorkspaceNote'),
      step: t('signingIn.stepWorkspace'),
    },
  ];
  const cur = phase <= 0 ? phases[0] : phases[1];

  return (
    <AuthLayout topProgress footer={t('signingIn.helper')}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: '22px' }}>
        <CircularProgress size={34} thickness={4} aria-hidden />
      </Box>

      <Box sx={{ textAlign: 'center', minHeight: 46 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: (theme) => theme.typography.button.fontFamily,
            fontWeight: 600,
            fontSize: 18,
            lineHeight: '27px',
            color: 'text.primary',
          }}
        >
          {cur.title}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: '4px', color: 'text.secondary' }}>
          {cur.note}
        </Typography>
      </Box>

      <Stack spacing={1.25} sx={{ mt: '26px' }}>
        {phases.map((p, i) => (
          <Stack key={p.step} direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
            <StepNode done={i < phase} active={i === phase} />
            <Typography
              variant="body2"
              sx={{ color: i <= phase ? 'text.primary' : 'text.secondary' }}
            >
              {p.step}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </AuthLayout>
  );
}
